import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { t } from '../../../lib/i18n';
import {
  adminTopUpRequestsQueryKey,
  fetchTopUpRequests,
  financeErrorMessage,
  refusalCode,
  rejectTopUpRequest,
  type AdminTopUpRequest,
} from '../api';
import { trapTabKey, useReturnFocusOnUnmount } from '../dialog-focus';
import { formatChf } from '../utils';
import { PanelEmpty, PanelError, PanelLoading } from './panel-state';
import { TopUpConfirmDialog } from './topup-confirm-dialog';

/** `11:32 · 27.09` — reference date, in the operator's clock. */
function formatCreated(date: Date): string {
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * «Відхилити заявку» — a reason (≥ 3 characters, `TopUpRejectSchema`), focus
 * returns to the row's «Відхилити» button on cancel or on a settled mutation.
 */
function RejectDialog({
  request,
  onClose,
}: {
  request: AdminTopUpRequest;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useReturnFocusOnUnmount();
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const mutation = useMutation({
    mutationFn: () => rejectTopUpRequest(request.id, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminTopUpRequestsQueryKey('open') });
      onClose();
    },
    onError: (error) => {
      // Same fork as the confirm dialog: someone else already closed this
      // request, so the queue is refetched for real rather than left stale
      // behind a translated sentence that only PROMISED a refresh.
      if (refusalCode(error) === 'TOPUP_NOT_OPEN') {
        void queryClient.invalidateQueries({ queryKey: adminTopUpRequestsQueryKey('open') });
      }
    },
  });

  const reasonValid = reason.trim().length >= 3;
  const notOpen = refusalCode(mutation.error) === 'TOPUP_NOT_OPEN';
  const errorText = mutation.error
    ? notOpen
      ? t('adminFinance.topUp.confirm.alreadyClosed')
      : (financeErrorMessage(mutation.error) ?? t('adminFinance.topUp.reject.failed'))
    : null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(dialogRef.current, event);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="flex w-full max-w-[420px] flex-col gap-3.5 rounded-cf-lg border border-cf-line bg-cf-surface p-5 text-left leading-[normal] shadow-[0_24px_60px_rgba(0,0,0,0.16)]"
      >
        <h2 id={titleId} className="text-[18px] font-semibold tracking-[-0.02em]">
          {t('adminFinance.topUp.reject.title', { reference: request.reference })}
        </h2>
        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-cf-ink-muted">{t('adminFinance.topUp.reject.reasonLabel')}</span>
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            className="rounded-cf-md border border-cf-line bg-cf-surface px-3 py-2 text-[14px]"
          />
        </label>
        {errorText && (
          <p role="alert" className="text-[12px] text-cf-danger">
            {errorText}
          </p>
        )}
        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[13px] font-semibold"
          >
            {t('adminFinance.topUp.reject.cancel')}
          </button>
          <button
            type="button"
            disabled={!reasonValid || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-10 rounded-cf-md bg-cf-danger px-3.5 text-[13px] font-semibold text-white disabled:opacity-40"
          >
            {mutation.isPending
              ? t('adminFinance.topUp.reject.pending')
              : t('adminFinance.topUp.reject.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * «Заявки на поповнення» — the manager's desk above «Депозити й режими».
 *
 * Every OPEN request: reference, client, amount, the method the client
 * announced, when, and two actions. Confirming records how the money
 * actually arrived (`TopUpConfirmDialog`); rejecting asks why. Both close the
 * request and both refetch this SAME query — `['admin-topup-requests', 'open']`
 * — the sidebar badge shares, so a confirm/reject here updates the pill
 * without a second request.
 */
export function TopUpRequestsPanel() {
  const query = useQuery({
    queryKey: adminTopUpRequestsQueryKey('open'),
    queryFn: () => fetchTopUpRequests('open'),
  });
  const [confirming, setConfirming] = useState<AdminTopUpRequest | null>(null);
  const [rejecting, setRejecting] = useState<AdminTopUpRequest | null>(null);

  if (query.isPending) return <PanelLoading />;
  if (query.isError) {
    return <PanelError error={query.error} fallbackKey="adminFinance.topUp.error.load" />;
  }

  const requests = query.data;
  if (requests.length === 0) {
    return <PanelEmpty messageKey="adminFinance.topUp.empty" />;
  }

  return (
    <section
      role="table"
      aria-label={t('adminFinance.topUp.tableAria')}
      className="overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface leading-[normal]"
    >
      <div
        role="row"
        className="grid min-w-[760px] grid-cols-[130px_minmax(0,1.4fr)_110px_100px_120px_180px] items-center gap-3.5 border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase"
      >
        <span role="columnheader">{t('adminFinance.topUp.columns.reference')}</span>
        <span role="columnheader">{t('adminFinance.topUp.columns.client')}</span>
        <span role="columnheader" className="text-right">
          {t('adminFinance.topUp.columns.amount')}
        </span>
        <span role="columnheader">{t('adminFinance.topUp.columns.method')}</span>
        <span role="columnheader">{t('adminFinance.topUp.columns.created')}</span>
        <span role="columnheader" className="text-right">
          {t('adminFinance.topUp.columns.action')}
        </span>
      </div>
      {requests.map((request) => (
        <div
          key={request.id}
          role="row"
          data-testid={`topup-request-${request.id}`}
          className="grid min-w-[760px] grid-cols-[130px_minmax(0,1.4fr)_110px_100px_120px_180px] items-center gap-3.5 border-b border-cf-line-soft px-[18px] py-3 text-[13px]"
        >
          <span role="cell" className="truncate font-mono text-[12px]">
            {request.reference}
          </span>
          <span role="cell" className="truncate">
            {request.clientName ?? request.userId}
          </span>
          <span role="cell" className="text-right font-mono">
            {formatChf(request.amountChf)}
          </span>
          <span role="cell">
            <span className="inline-flex w-fit rounded-cf-sm bg-cf-fill px-2 py-1 font-mono text-[10px] tracking-[0.08em] text-cf-ink-muted uppercase">
              {t(`adminFinance.topUp.method.${request.method}`)}
            </span>
          </span>
          <span role="cell" className="font-mono text-[12px] text-cf-ink-subtle">
            {formatCreated(request.createdAt)}
          </span>
          <span role="cell" className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirming(request)}
              className="h-8 rounded-cf-md border border-cf-brand bg-cf-brand px-3 text-[12px] font-semibold whitespace-nowrap text-white hover:bg-cf-brand-hover"
            >
              {t('adminFinance.topUp.action.confirm')}
            </button>
            <button
              type="button"
              onClick={() => setRejecting(request)}
              className="h-8 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[12px] font-semibold whitespace-nowrap text-cf-ink hover:border-cf-ink"
            >
              {t('adminFinance.topUp.action.reject')}
            </button>
          </span>
        </div>
      ))}

      {confirming && <TopUpConfirmDialog request={confirming} onClose={() => setConfirming(null)} />}
      {rejecting && <RejectDialog request={rejecting} onClose={() => setRejecting(null)} />}
    </section>
  );
}
